import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ExerciseService from '../../services/exerciseService';

const ExerciseLibrary = () => {
  const { token } = useContext(AuthContext);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [muscleFilter, setMuscleFilter] = useState('');
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [categories, setCategories] = useState([]);
  const [difficulties, setDifficulties] = useState([]);
  const [muscles, setMuscles] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExercise, setNewExercise] = useState({
    name: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    muscles: [],
    equipment: ''
  });

  useEffect(() => {
    fetchExercises();
    fetchFilters();
  }, [token, currentPage, searchTerm, categoryFilter, difficultyFilter, muscleFilter, showCustomOnly]);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      
      const queryParams = {};
      
      if (searchTerm) queryParams.search = searchTerm;
      if (categoryFilter) queryParams.category = categoryFilter;
      if (difficultyFilter) queryParams.difficulty = difficultyFilter;
      if (muscleFilter) queryParams.muscle = muscleFilter;
      if (showCustomOnly) queryParams.custom = true;
      
      queryParams.page = currentPage;
      queryParams.limit = 12;
      
      const data = await ExerciseService.getAllExercises(token, queryParams);
      
      if (data.success) {
        setExercises(data.exercises || []);
        setTotalPages(data.pagination.totalPages || 1);
      } else {
        toast.error(data.message || 'Failed to fetch exercises');
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const categoriesData = await ExerciseService.getExerciseCategories(token);
      
      if (categoriesData.success) {
        setCategories(categoriesData.categories || []);
      }
      
      const difficultiesData = await ExerciseService.getExerciseDifficulties(token);
      
      if (difficultiesData.success) {
        setDifficulties(difficultiesData.difficulties || []);
      }
      
      const musclesData = await ExerciseService.getExerciseMuscles(token);
      
      if (musclesData.success) {
        setMuscles(musclesData.muscles || []);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExercise({ ...newExercise, [name]: value });
  };

  const handleMuscleChange = (e) => {
    const selectedMuscles = Array.from(e.target.selectedOptions, option => option.value);
    setNewExercise({ ...newExercise, muscles: selectedMuscles });
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    
    if (!newExercise.name || !newExercise.category) {
      toast.error('Please enter exercise name and category');
      return;
    }
    
    try {
      const data = await ExerciseService.createExercise(newExercise, token);
      
      if (data.success) {
        toast.success('Exercise added successfully');
        setNewExercise({
          name: '',
          description: '',
          category: '',
          difficulty: 'beginner',
          muscles: [],
          equipment: ''
        });
        setShowAddForm(false);
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to add exercise');
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm('Are you sure you want to delete this custom exercise?')) {
      return;
    }
    
    try {
      const data = await ExerciseService.deleteExercise(exerciseId, token);
      
      if (data.success) {
        toast.success('Exercise deleted successfully');
        fetchExercises();
      } else {
        toast.error(data.message || 'Failed to delete exercise');
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="exercise-library">
      <h1>Exercise Library</h1>
      
      {/* Filters and Search */}
      <div className="filters-container">
        <form className="search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search exercises..."
          />
          <button type="submit" className="btn btn-primary btn-sm">
            Search
          </button>
        </form>
        
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="categoryFilter">Category:</label>
            <select
              id="categoryFilter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="difficultyFilter">Difficulty:</label>
            <select
              id="difficultyFilter"
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
            >
              <option value="">All Difficulties</option>
              {difficulties.map((difficulty, index) => (
                <option key={index} value={difficulty}>
                  {difficulty}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Exercise Grid */}
      {loading && exercises.length === 0 ? (
        <div className="loading">Loading exercises...</div>
      ) : exercises.length === 0 ? (
        <div className="no-exercises">
          <p>No exercises found matching your criteria.</p>
        </div>
      ) : (
        <div className="exercise-grid">
          {exercises.map(exercise => (
            <div key={exercise._id} className="exercise-card">
              <h3>{exercise.name}</h3>
              <p className="category">{exercise.category}</p>
              <p className="difficulty">Difficulty: {exercise.difficulty}</p>
              {exercise.description && (
                <p className="description">{exercise.description}</p>
              )}
              {exercise.muscles && exercise.muscles.length > 0 && (
                <p className="muscles">
                  <strong>Muscles:</strong> {exercise.muscles.join(', ')}
                </p>
              )}
              {exercise.isCustom && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteExercise(exercise._id)}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-light btn-sm"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-light btn-sm"
          >
            Next
          </button>
        </div>
      )}
      
      {/* Add Exercise Button */}
      <div className="add-exercise-button">
        <button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add Custom Exercise'}
        </button>
      </div>
      
      {/* Add Exercise Form */}
      {showAddForm && (
        <div className="add-exercise-form">
          <h2>Add Custom Exercise</h2>
          <form onSubmit={handleAddExercise}>
            <div className="form-group">
              <label htmlFor="name">Exercise Name*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={newExercise.name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={newExercise.description}
                onChange={handleInputChange}
                rows="3"
              ></textarea>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Category*</label>
              <input
                type="text"
                id="category"
                name="category"
                value={newExercise.category}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <button type="submit" className="btn btn-success">
              Add Exercise
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ExerciseLibrary;
